import { z } from "zod";

import {
  socialPostFontStyleSchema,
  socialPostImageFitSchema,
  socialPostSceneBlockV1Schema,
  socialPostSceneCanvasV1Schema,
  socialPostSceneFrameBlockV1Schema,
  socialPostSceneImageBlockV1Schema,
  socialPostSceneLogoBlockV1Schema,
  socialPostScenePlanV1Schema,
  socialPostSceneRectBlockV1Schema,
  socialPostSceneShapeBlockV1Schema,
  socialPostSceneTextBlockV1Schema,
  socialPostTextAlignSchema,
  socialPostTextWrapSchema,
  socialPostVerticalAlignSchema,
  type SocialPostSceneBlockV1,
  type SocialPostScenePlanV1,
} from "./social-post-scene.ts";

const editableBlockBaseSchema = z
  .object({
    layerName: socialPostSceneImageBlockV1Schema.shape.layerName,
    x: z.number().finite(),
    y: z.number().finite(),
    width: z.number().finite().positive(),
    height: z.number().finite().positive(),
    rotation: z.number().finite(),
    opacity: z.number().finite().min(0).max(1),
    zIndex: z.number().int().nonnegative(),
    visible: z.boolean(),
  })
  .partial();

const unwrapToolFieldSchema = (schema: z.ZodType): z.ZodType => {
  if (schema instanceof z.ZodArray) {
    return schema;
  }

  if ("unwrap" in schema && typeof schema.unwrap === "function") {
    return unwrapToolFieldSchema(schema.unwrap() as z.ZodType);
  }

  return schema;
};

const toRequiredNullableToolShape = (shape: z.ZodRawShape): z.ZodRawShape =>
  Object.fromEntries(
    Object.entries(shape).map(([key, schema]) => [
      key,
      unwrapToolFieldSchema(schema as z.ZodType).nullable(),
    ]),
  );

const withMissingToolFields = <Schema extends z.ZodType>(
  defaults: Record<string, unknown>,
  schema: Schema,
) =>
  z.preprocess((value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }

    return { ...defaults, ...value };
  }, schema);

const toRequiredNullableToolObject = (shape: z.ZodRawShape) =>
  withMissingToolFields(
    Object.fromEntries(Object.keys(shape).map((key) => [key, null])),
    z.object(toRequiredNullableToolShape(shape)).strict(),
  );

const editableTextBlockChangesSchema = editableBlockBaseSchema.extend({
  text: socialPostSceneTextBlockV1Schema.shape.text.optional(),
  fill: socialPostSceneTextBlockV1Schema.shape.fill.optional(),
  stroke: socialPostSceneTextBlockV1Schema.shape.stroke,
  strokeWidth: socialPostSceneTextBlockV1Schema.shape.strokeWidth,
  fontFamily: socialPostSceneTextBlockV1Schema.shape.fontFamily.optional(),
  fontSize: socialPostSceneTextBlockV1Schema.shape.fontSize.optional(),
  fontStyle: socialPostFontStyleSchema.optional(),
  align: socialPostTextAlignSchema.optional(),
  verticalAlign: socialPostVerticalAlignSchema.optional(),
  lineHeight: socialPostSceneTextBlockV1Schema.shape.lineHeight.optional(),
  letterSpacing: socialPostSceneTextBlockV1Schema.shape.letterSpacing.optional(),
  padding: socialPostSceneTextBlockV1Schema.shape.padding.optional(),
  wrap: socialPostTextWrapSchema.optional(),
});

const editableImageBlockChangesSchema = editableBlockBaseSchema.extend({
  fit: socialPostImageFitSchema.optional(),
  cornerRadius: socialPostSceneImageBlockV1Schema.shape.cornerRadius.optional(),
});

const editableLogoBlockChangesSchema = editableBlockBaseSchema.extend({
  assetObjectKey: socialPostSceneLogoBlockV1Schema.shape.assetObjectKey,
  fit: socialPostImageFitSchema.optional(),
  cornerRadius: socialPostSceneLogoBlockV1Schema.shape.cornerRadius.optional(),
});

const editableRectBlockChangesSchema = editableBlockBaseSchema.extend({
  fill: socialPostSceneRectBlockV1Schema.shape.fill.optional(),
  stroke: socialPostSceneRectBlockV1Schema.shape.stroke.optional(),
  strokeWidth: socialPostSceneRectBlockV1Schema.shape.strokeWidth.optional(),
  cornerRadius: socialPostSceneRectBlockV1Schema.shape.cornerRadius.optional(),
});

const editableShapeBlockChangesSchema = editableBlockBaseSchema.extend({
  fill: socialPostSceneShapeBlockV1Schema.shape.fill.optional(),
  stroke: socialPostSceneShapeBlockV1Schema.shape.stroke.optional(),
  strokeWidth: socialPostSceneShapeBlockV1Schema.shape.strokeWidth.optional(),
  cornerRadius: socialPostSceneShapeBlockV1Schema.shape.cornerRadius.optional(),
  lineCap: socialPostSceneShapeBlockV1Schema.shape.lineCap.optional(),
  lineJoin: socialPostSceneShapeBlockV1Schema.shape.lineJoin.optional(),
  dash: socialPostSceneShapeBlockV1Schema.shape.dash.optional(),
  points: socialPostSceneShapeBlockV1Schema.shape.points.optional(),
  closed: socialPostSceneShapeBlockV1Schema.shape.closed.optional(),
  tension: socialPostSceneShapeBlockV1Schema.shape.tension.optional(),
  sides: socialPostSceneShapeBlockV1Schema.shape.sides.optional(),
  radius: socialPostSceneShapeBlockV1Schema.shape.radius.optional(),
  innerRadius: socialPostSceneShapeBlockV1Schema.shape.innerRadius.optional(),
  outerRadius: socialPostSceneShapeBlockV1Schema.shape.outerRadius.optional(),
  angle: socialPostSceneShapeBlockV1Schema.shape.angle.optional(),
  clockwise: socialPostSceneShapeBlockV1Schema.shape.clockwise.optional(),
  pathData: socialPostSceneShapeBlockV1Schema.shape.pathData.optional(),
});

const editableFrameBlockChangesSchema = editableBlockBaseSchema.extend({
  fill: socialPostSceneFrameBlockV1Schema.shape.fill.optional(),
  stroke: socialPostSceneFrameBlockV1Schema.shape.stroke.optional(),
  strokeWidth: socialPostSceneFrameBlockV1Schema.shape.strokeWidth.optional(),
  cornerRadius: socialPostSceneFrameBlockV1Schema.shape.cornerRadius.optional(),
  insetPadding: socialPostSceneFrameBlockV1Schema.shape.insetPadding.optional(),
});

const editableCanvasChangesSchema = z.object({
  backgroundFill: socialPostSceneCanvasV1Schema.shape.backgroundFill.optional(),
});

const updateCanvasOperationSchema = z.object({
  type: z.literal("update_canvas"),
  changes: editableCanvasChangesSchema,
});

const updateCaptionOperationSchema = z.object({
  type: z.literal("update_caption"),
  caption: z.string().trim().min(1).nullable(),
  hashtags: z.array(z.string().trim().min(1)).optional(),
});

const blockIdSchema = z.string().trim().min(1);

const updateTextBlockOperationSchema = z.object({
  type: z.literal("update_text_block"),
  blockId: blockIdSchema,
  changes: editableTextBlockChangesSchema,
});

const updateImageBlockOperationSchema = z.object({
  type: z.literal("update_image_block"),
  blockId: blockIdSchema,
  changes: editableImageBlockChangesSchema,
});

const updateLogoBlockOperationSchema = z.object({
  type: z.literal("update_logo_block"),
  blockId: blockIdSchema,
  changes: editableLogoBlockChangesSchema,
});

const updateRectBlockOperationSchema = z.object({
  type: z.literal("update_rect_block"),
  blockId: blockIdSchema,
  changes: editableRectBlockChangesSchema,
});

const updateShapeBlockOperationSchema = z.object({
  type: z.literal("update_shape_block"),
  blockId: blockIdSchema,
  changes: editableShapeBlockChangesSchema,
});

const updateFrameBlockOperationSchema = z.object({
  type: z.literal("update_frame_block"),
  blockId: blockIdSchema,
  changes: editableFrameBlockChangesSchema,
});

const addBlockOperationSchema = z.object({
  type: z.literal("add_block"),
  block: socialPostSceneBlockV1Schema,
});

const removeBlockOperationSchema = z.object({
  type: z.literal("remove_block"),
  blockId: blockIdSchema,
});

export const socialPostTemplateEditOperationSchema = z.discriminatedUnion("type", [
  updateCanvasOperationSchema,
  updateCaptionOperationSchema,
  updateTextBlockOperationSchema,
  updateImageBlockOperationSchema,
  updateLogoBlockOperationSchema,
  updateRectBlockOperationSchema,
  updateShapeBlockOperationSchema,
  updateFrameBlockOperationSchema,
  addBlockOperationSchema,
  removeBlockOperationSchema,
]);
export type SocialPostTemplateEditOperation = z.infer<typeof socialPostTemplateEditOperationSchema>;

export const socialPostTemplateUpdateOnlyEditOperationSchema = z.discriminatedUnion("type", [
  updateCanvasOperationSchema,
  updateCaptionOperationSchema,
  updateTextBlockOperationSchema,
  updateImageBlockOperationSchema,
  updateLogoBlockOperationSchema,
  updateRectBlockOperationSchema,
  updateShapeBlockOperationSchema,
  updateFrameBlockOperationSchema,
]);
export type SocialPostTemplateUpdateOnlyEditOperation = z.infer<
  typeof socialPostTemplateUpdateOnlyEditOperationSchema
>;

const updateCanvasToolOperationSchema = z
  .object({
    type: z.literal("update_canvas"),
    changes: toRequiredNullableToolObject(editableCanvasChangesSchema.shape),
  })
  .strict();
const updateCaptionToolOperationSchema = z
  .object({
    type: z.literal("update_caption"),
    caption: updateCaptionOperationSchema.shape.caption,
    hashtags: z.array(z.string().trim().min(1)).nullable(),
  })
  .strict();
const updateTextBlockToolOperationSchema = z
  .object({
    type: z.literal("update_text_block"),
    blockId: blockIdSchema,
    changes: toRequiredNullableToolObject(editableTextBlockChangesSchema.shape),
  })
  .strict();
const updateImageBlockToolOperationSchema = z
  .object({
    type: z.literal("update_image_block"),
    blockId: blockIdSchema,
    changes: toRequiredNullableToolObject(editableImageBlockChangesSchema.shape),
  })
  .strict();
const updateLogoBlockToolOperationSchema = z
  .object({
    type: z.literal("update_logo_block"),
    blockId: blockIdSchema,
    changes: toRequiredNullableToolObject(editableLogoBlockChangesSchema.shape),
  })
  .strict();
const updateRectBlockToolOperationSchema = z
  .object({
    type: z.literal("update_rect_block"),
    blockId: blockIdSchema,
    changes: toRequiredNullableToolObject(editableRectBlockChangesSchema.shape),
  })
  .strict();
const updateShapeBlockToolOperationSchema = z
  .object({
    type: z.literal("update_shape_block"),
    blockId: blockIdSchema,
    changes: toRequiredNullableToolObject(editableShapeBlockChangesSchema.shape),
  })
  .strict();
const updateFrameBlockToolOperationSchema = z
  .object({
    type: z.literal("update_frame_block"),
    blockId: blockIdSchema,
    changes: toRequiredNullableToolObject(editableFrameBlockChangesSchema.shape),
  })
  .strict();

export const socialPostTemplateUpdateOnlyEditToolOperationSchema = z.discriminatedUnion("type", [
  updateCanvasToolOperationSchema,
  updateCaptionToolOperationSchema,
  updateTextBlockToolOperationSchema,
  updateImageBlockToolOperationSchema,
  updateLogoBlockToolOperationSchema,
  updateRectBlockToolOperationSchema,
  updateShapeBlockToolOperationSchema,
  updateFrameBlockToolOperationSchema,
]);
export type SocialPostTemplateUpdateOnlyEditToolOperation = z.infer<
  typeof socialPostTemplateUpdateOnlyEditToolOperationSchema
>;

export const socialPostTemplateUpdateOnlyEditToolInputSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }

    const record = value as Record<string, unknown>;
    const operations = Array.isArray(record.operations)
      ? record.operations.map((operation) => {
          if (!operation || typeof operation !== "object" || Array.isArray(operation)) {
            return operation;
          }

          const operationRecord = operation as Record<string, unknown>;
          if (operationRecord.type !== "update_caption") {
            return operation;
          }

          return { hashtags: null, ...operationRecord };
        })
      : record.operations;

    return { ...record, operations };
  },
  z
    .object({
      summary: z.string().trim().min(1),
      operations: z.array(socialPostTemplateUpdateOnlyEditToolOperationSchema).min(1).max(20),
    })
    .strict(),
);
export type SocialPostTemplateUpdateOnlyEditToolInput = z.infer<
  typeof socialPostTemplateUpdateOnlyEditToolInputSchema
>;

export const socialPostTemplateEditBatchSchema = z.object({
  summary: z.string().trim().min(1),
  operations: z.array(socialPostTemplateEditOperationSchema).min(1).max(20),
});
export type SocialPostTemplateEditBatch = z.infer<typeof socialPostTemplateEditBatchSchema>;

export const socialPostTemplateUpdateOnlyEditBatchSchema = z.object({
  summary: socialPostTemplateEditBatchSchema.shape.summary,
  operations: z.array(socialPostTemplateUpdateOnlyEditOperationSchema).min(1).max(20),
});
export type SocialPostTemplateUpdateOnlyEditBatch = z.infer<
  typeof socialPostTemplateUpdateOnlyEditBatchSchema
>;

const stripNullProperties = (value: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(value).filter(([, item]) => item !== null));

export const normalizeSocialPostTemplateUpdateOnlyEditToolInput = (
  input: unknown,
): SocialPostTemplateUpdateOnlyEditBatch => {
  const batch = socialPostTemplateUpdateOnlyEditToolInputSchema.parse(input);

  return socialPostTemplateUpdateOnlyEditBatchSchema.parse({
    summary: batch.summary,
    operations: batch.operations.map((operation) => {
      switch (operation.type) {
        case "update_caption":
          return {
            type: operation.type,
            caption: operation.caption,
            ...(operation.hashtags === null ? {} : { hashtags: operation.hashtags }),
          };
        case "update_canvas":
          return {
            type: operation.type,
            changes: stripNullProperties(operation.changes),
          };
        default:
          return {
            type: operation.type,
            blockId: operation.blockId,
            changes: stripNullProperties(operation.changes),
          };
      }
    }),
  });
};

export const socialPostDraftImageAssetOverrideSchema = z.object({
  blockId: z.string().trim().min(1),
  generatedAssetId: z.uuid(),
});
export type SocialPostDraftImageAssetOverride = z.infer<
  typeof socialPostDraftImageAssetOverrideSchema
>;

export const socialPostCarouselSlideEditableDraftSchema = z.object({
  slideId: z.string().trim().min(1),
  slideIndex: z.number().int().positive(),
  plan: socialPostScenePlanV1Schema,
  imageAssetOverrides: z.array(socialPostDraftImageAssetOverrideSchema).default([]),
});
export type SocialPostCarouselSlideEditableDraft = z.infer<
  typeof socialPostCarouselSlideEditableDraftSchema
>;

export const socialPostVariantEditableDraftSchema = z.object({
  generatedAssetId: z.uuid(),
  plan: socialPostScenePlanV1Schema,
  imageAssetOverrides: z.array(socialPostDraftImageAssetOverrideSchema).default([]),
  carouselSlides: z.array(socialPostCarouselSlideEditableDraftSchema).default([]),
});
export type SocialPostVariantEditableDraft = z.infer<typeof socialPostVariantEditableDraftSchema>;

export const socialPostEditableDraftSchema = z.object({
  version: z.literal(1),
  caption: z.string().nullable(),
  hashtags: z.array(z.string().trim().min(1)).default([]),
  plan: socialPostScenePlanV1Schema,
  imageAssetOverrides: z.array(socialPostDraftImageAssetOverrideSchema).default([]),
  carouselSlides: z.array(socialPostCarouselSlideEditableDraftSchema).default([]),
  variantDrafts: z.array(socialPostVariantEditableDraftSchema).default([]),
});
export type SocialPostEditableDraft = z.infer<typeof socialPostEditableDraftSchema>;

export const createSocialPostEditableDraft = (input: {
  plan: SocialPostScenePlanV1;
  caption?: string | null;
  hashtags?: string[];
  imageAssetOverrides?: SocialPostDraftImageAssetOverride[];
  carouselSlides?: SocialPostCarouselSlideEditableDraft[];
  variantDrafts?: SocialPostVariantEditableDraft[];
}): SocialPostEditableDraft =>
  socialPostEditableDraftSchema.parse({
    version: 1,
    caption: input.caption ?? null,
    hashtags: input.hashtags ?? [],
    plan: input.plan,
    imageAssetOverrides: input.imageAssetOverrides ?? [],
    carouselSlides: input.carouselSlides ?? [],
    variantDrafts: input.variantDrafts ?? [],
  });

export const applySocialPostTemplateEditBatch = (
  draft: SocialPostEditableDraft,
  batch: SocialPostTemplateEditBatch,
): SocialPostEditableDraft => {
  let nextDraft = socialPostEditableDraftSchema.parse(draft);

  for (const operation of batch.operations) {
    nextDraft = applySocialPostTemplateEditOperation(nextDraft, operation);
  }

  return nextDraft;
};

export const applySocialPostTemplateEditOperation = (
  draft: SocialPostEditableDraft,
  operation: SocialPostTemplateEditOperation,
): SocialPostEditableDraft => {
  switch (operation.type) {
    case "update_canvas":
      return socialPostEditableDraftSchema.parse({
        ...draft,
        plan: socialPostScenePlanV1Schema.parse({
          ...draft.plan,
          scene: {
            ...draft.plan.scene,
            canvas: {
              ...draft.plan.scene.canvas,
              ...operation.changes,
            },
          },
        }),
      });
    case "update_caption":
      return socialPostEditableDraftSchema.parse({
        ...draft,
        caption: operation.caption,
        hashtags: operation.hashtags ?? draft.hashtags,
      });
    case "add_block":
      return socialPostEditableDraftSchema.parse({
        ...draft,
        plan: socialPostScenePlanV1Schema.parse({
          ...draft.plan,
          scene: {
            ...draft.plan.scene,
            blocks: addBlockToScene(draft.plan.scene.blocks, operation.block),
          },
        }),
      });
    case "remove_block":
      return socialPostEditableDraftSchema.parse({
        ...draft,
        plan: socialPostScenePlanV1Schema.parse({
          ...draft.plan,
          scene: {
            ...draft.plan.scene,
            blocks: removeBlockFromScene(draft.plan.scene.blocks, operation.blockId),
          },
        }),
      });
    default: {
      let didUpdate = false;
      const blocks = updateBlocksById(draft.plan.scene.blocks, operation.blockId, (block) => {
        switch (operation.type) {
          case "update_text_block":
            if (block.type !== "text") {
              return block;
            }
            didUpdate = true;
            return socialPostSceneTextBlockV1Schema.parse({ ...block, ...operation.changes });
          case "update_image_block":
            if (block.type !== "image") {
              return block;
            }
            didUpdate = true;
            return socialPostSceneImageBlockV1Schema.parse({ ...block, ...operation.changes });
          case "update_logo_block":
            if (block.type !== "logo") {
              return block;
            }
            didUpdate = true;
            return socialPostSceneLogoBlockV1Schema.parse({ ...block, ...operation.changes });
          case "update_rect_block":
            if (block.type !== "rect") {
              return block;
            }
            didUpdate = true;
            return socialPostSceneRectBlockV1Schema.parse({ ...block, ...operation.changes });
          case "update_shape_block":
            if (block.type !== "shape") {
              return block;
            }
            didUpdate = true;
            return socialPostSceneShapeBlockV1Schema.parse({ ...block, ...operation.changes });
          case "update_frame_block":
            if (block.type !== "frame") {
              return block;
            }
            didUpdate = true;
            return socialPostSceneFrameBlockV1Schema.parse({ ...block, ...operation.changes });
          default:
            return block;
        }
      });

      if (!didUpdate) {
        throw new Error(`Editable block not found for operation: ${operation.blockId}`);
      }

      return socialPostEditableDraftSchema.parse({
        ...draft,
        plan: socialPostScenePlanV1Schema.parse({
          ...draft.plan,
          scene: {
            ...draft.plan.scene,
            blocks,
          },
        }),
      });
    }
  }
};

const updateBlocksById = (
  blocks: SocialPostSceneBlockV1[],
  blockId: string,
  updater: (block: SocialPostSceneBlockV1) => SocialPostSceneBlockV1,
): SocialPostSceneBlockV1[] =>
  blocks.map((block) => {
    if (block.id === blockId) {
      return socialPostSceneBlockV1Schema.parse(updater(block));
    }

    return block;
  });

function addBlockToScene(blocks: SocialPostSceneBlockV1[], block: SocialPostSceneBlockV1) {
  assertUniqueNewBlockId(blocks, block.id);
  const maxZ = blocks.reduce((max, b) => Math.max(max, b.zIndex), -1);
  const parsed = socialPostSceneBlockV1Schema.parse({ ...block, zIndex: maxZ + 1 });
  return [parsed, ...blocks];
}

function removeBlockFromScene(blocks: SocialPostSceneBlockV1[], blockId: string) {
  const rootIndex = blocks.findIndex((block) => block.id === blockId);
  if (rootIndex >= 0) {
    const nextBlocks = blocks.filter((block) => block.id !== blockId);
    if (nextBlocks.length === 0) {
      throw new Error("Cannot remove the last scene block.");
    }
    return normalizeZIndexes(nextBlocks.toSorted(sortBlocksFrontToBack));
  }

  throw new Error(`Editable block not found for operation: ${blockId}`);
}

function findBlockEntry(blocks: SocialPostSceneBlockV1[], blockId: string) {
  for (const block of blocks) {
    if (block.id === blockId) {
      return { block };
    }
  }

  return null;
}

function assertUniqueNewBlockId(blocks: SocialPostSceneBlockV1[], blockId: string) {
  if (findBlockEntry(blocks, blockId)) {
    throw new Error(`Block id already exists: ${blockId}`);
  }
}

function normalizeZIndexes<T extends { zIndex: number }>(blocks: T[]): T[] {
  return blocks.map((block, index) => ({ ...block, zIndex: blocks.length - 1 - index }));
}

function sortBlocksFrontToBack<T extends { zIndex: number }>(left: T, right: T) {
  return right.zIndex - left.zIndex;
}
